import React, { useEffect, useRef, useCallback, useState } from 'react';
import * as d3 from 'd3';

const COLORS = {
  SECURITY_TRAILS: '#0969da',
  PUBLICWWW: '#cf222e',
};

const TRACK = '#e8ecf0';
const OVER_LIMIT = '#b35900';

function formatLimit(n) {
  if (n >= 1000) return d3.format(',')(n);
  return String(n);
}

function chartFonts() {
  if (typeof document === 'undefined') {
    return { ui: "'Source Sans 3', sans-serif", data: "'IBM Plex Mono', monospace" };
  }
  const cs = getComputedStyle(document.documentElement);
  return {
    ui: cs.getPropertyValue('--argus-chart-font-ui').trim() || "'Source Sans 3', sans-serif",
    data: cs.getPropertyValue('--argus-chart-font-data').trim() || "'IBM Plex Mono', monospace",
  };
}

/**
 * Horizontal 100%-of-cap bars: each row's bar width = that API's limit (daily or monthly).
 * Filled segment = used; remainder = unused (stacked to 100% of track).
 */
export default function ApiLimitsOverallChart({ overview }) {
  const wrapRef = useRef(null);
  const svgRef = useRef(null);
  const [dims, setDims] = useState({ width: 720 });

  const measure = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const w = el.clientWidth || 720;
    setDims({ width: Math.max(300, w) });
  }, []);

  useEffect(() => {
    measure();
    const ro = new ResizeObserver(() => measure());
    if (wrapRef.current) ro.observe(wrapRef.current);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  useEffect(() => {
    if (!overview || !overview.length || !svgRef.current) return;

    const { ui: fontUi, data: fontData } = chartFonts();

    const margin = { top: 10, right: 12, bottom: 10, left: 8 };
    const labelW = 160;
    /* Reserve enough width for right-anchored lines ("Month-to-date" + mono counts) so they never draw over the track */
    const valueColW = 288;
    const gapBarValue = 20;
    const barH = 24;
    const barY = 24;
    /* Ticks directly under the bar; period + usage sit below tick labels (right-aligned), never on the track */
    const tickLabelOffset = 22;
    const statsBelowTicks = 12;
    const statsLineGap = 15;
    const rowH = 128;
    const width = dims.width;
    const innerW = width - margin.left - margin.right;
    const barW = Math.max(100, innerW - labelW - valueColW - gapBarValue);
    const height = overview.length * rowH + margin.top + margin.bottom;
    const valueX = width - margin.right;
    const tickTextY = barY + barH + tickLabelOffset;
    const statsLine1Y = tickTextY + statsBelowTicks;
    const statsLine2Y = statsLine1Y + statsLineGap;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', height);

    const root = svg.append('g').attr('transform', `translate(0,${margin.top})`);

    overview.forEach((row, i) => {
      const y0 = i * rowH;
      const color = COLORS[row.provider] || '#555';
      const limit = Math.max(1, row.limit);
      const used = Math.max(0, row.used);
      const over = used > limit;
      const fillW = over ? barW : (used / limit) * barW;
      const pct = (used / limit) * 100;

      const g = root.append('g').attr('transform', `translate(0,${y0})`);

      const nameY = barY + barH / 2;
      const bx = margin.left + labelW;

      g.append('text')
        .attr('x', margin.left)
        .attr('y', nameY)
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', 'start')
        .attr('fill', '#24292f')
        .attr('font-family', fontUi)
        .attr('font-size', 14)
        .attr('font-weight', 600)
        .text(row.name);
      g.append('rect')
        .attr('x', bx)
        .attr('y', barY)
        .attr('width', barW)
        .attr('height', barH)
        .attr('rx', 4)
        .attr('fill', TRACK);

      g.append('rect')
        .attr('x', bx)
        .attr('y', barY)
        .attr('width', over ? barW : fillW)
        .attr('height', barH)
        .attr('rx', 4)
        .attr('fill', over ? OVER_LIMIT : color);

      if (over) {
        g.append('rect')
          .attr('x', bx)
          .attr('y', barY)
          .attr('width', barW)
          .attr('height', barH)
          .attr('rx', 4)
          .attr('fill', 'none')
          .attr('stroke', '#cf222e')
          .attr('stroke-width', 2);
      }

      const usedStr = formatLimit(used);
      const limStr = formatLimit(limit);
      const statsLabel = `${usedStr} / ${limStr} (${pct.toFixed(1)}%)` + (over ? ' — over limit' : '');
      const periodLabel = row.basis === 'DAILY' ? 'Daily limit' : 'Month-to-date';

      g.append('text')
        .attr('x', valueX)
        .attr('y', statsLine1Y)
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', 'end')
        .attr('fill', '#57606a')
        .attr('font-family', fontUi)
        .attr('font-size', 11)
        .attr('font-weight', 400)
        .text(periodLabel);

      g.append('text')
        .attr('x', valueX)
        .attr('y', statsLine2Y)
        .attr('dominant-baseline', 'middle')
        .attr('text-anchor', 'end')
        .attr('fill', over ? '#cf222e' : '#24292f')
        .attr('font-family', fontData)
        .attr('font-size', 12)
        .attr('font-weight', 400)
        .text(statsLabel);

      const xScale = d3.scaleLinear().domain([0, limit]).range([0, barW]);
      const tickVals = [0, limit];
      if (limit > 2) {
        const mid = Math.round(limit / 2);
        if (mid > 0 && mid < limit) tickVals.splice(1, 0, mid);
      }
      tickVals.forEach((tv) => {
        const tx = bx + xScale(tv);
        g.append('line')
          .attr('x1', tx)
          .attr('x2', tx)
          .attr('y1', barY + barH)
          .attr('y2', barY + barH + 3)
          .attr('stroke', '#d0d7de');
        g.append('text')
          .attr('x', tx)
          .attr('y', barY + barH + tickLabelOffset)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'alphabetic')
          .attr('fill', '#6e7781')
          .attr('font-family', fontData)
          .attr('font-size', 11)
          .attr('font-weight', 400)
          .text(formatLimit(tv));
      });
    });
  }, [overview, dims]);

  if (!overview || !overview.length) {
    return (
      <div className="api-limits-overall-empty">
        No overview data available for the selected range.
      </div>
    );
  }

  return (
    <div className="api-limits-overall-wrap" ref={wrapRef}>
      <svg ref={svgRef} className="api-limits-overall-svg" role="img" aria-label="API usage versus limits" />
    </div>
  );
}
